import { useEffect, useState } from "react";
import { BellRing, Share } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import { api, errorMessage } from "@/lib/api";
import { disablePush, enablePush, needsHomeScreen, permission, pushSupported, syncPush } from "@/lib/push";

function Hint({ children, testid }) {
  return <div data-testid={testid} className="text-xs text-[#A1A1AA] mt-3 flex items-center gap-1.5 flex-wrap">{children}</div>;
}

export function useReminders() {
  const { user, setUser } = useAuth();
  const [busy, setBusy] = useState(false);
  const on = !!user?.reminders_enabled;

  const turnOn = async () => {
    setBusy(true);
    try {
      await enablePush();
      setUser((u) => ({ ...u, reminders_enabled: true, notif_prompt_seen: true }));
      api.post("/push/test").catch(() => {});
      toast.success("Reminders on");
      return true;
    } catch (err) {
      toast.error(err?.response ? errorMessage(err) : err.message);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const turnOff = async () => {
    setBusy(true);
    try {
      await disablePush();
      setUser((u) => ({ ...u, reminders_enabled: false }));
      toast.success("Reminders off");
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return { on, busy, turnOn, turnOff };
}

export function ReminderCard() {
  const { on, busy, turnOn, turnOff } = useReminders();
  const supported = pushSupported();
  const homeScreen = needsHomeScreen();
  const perm = permission();
  const blocked = perm === "denied";
  const needsDevice = on && supported && !homeScreen && perm === "default";

  useEffect(() => {
    if (on) syncPush().catch(() => {});
  }, [on]);

  const disabled = busy || (!on && (!supported || homeScreen || blocked));
  return (
    <div className="card p-4" data-testid="reminder-card">
      <label className="flex items-center justify-between gap-4 cursor-pointer">
        <div className="flex items-start gap-3">
          <BellRing size={18} className="text-[#E50914] mt-0.5 shrink-0" />
          <div>
            <div className="text-sm font-semibold">Training reminders</div>
            <div className="text-xs text-[#71717A] mt-0.5">A nudge to log your session at 2pm and 10pm.</div>
          </div>
        </div>
        <Switch data-testid="reminder-switch" checked={on} disabled={disabled} onCheckedChange={(v) => (v ? turnOn() : turnOff())} />
      </label>
      {needsDevice && (
        <button data-testid="reminder-enable-device" onClick={turnOn} className="mt-3 text-xs font-semibold text-white underline decoration-[#E50914] underline-offset-4">Allow notifications on this device</button>
      )}
      {!on && homeScreen && <Hint testid="reminder-ios-hint">On iPhone, first tap <Share size={13} className="text-[#3B82F6]" /> Share → <b>Add to Home Screen</b>, then open Roll SmartR from your home screen.</Hint>}
      {!on && !homeScreen && !supported && <Hint testid="reminder-unsupported">This browser doesn't support notifications.</Hint>}
      {blocked && <Hint testid="reminder-blocked">Notifications are blocked. Allow them for Roll SmartR in your phone settings.</Hint>}
    </div>
  );
}
